import React from "react";
import { useNavigate, useLocation } from "react-router-dom";

const MFA = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Expect email and sessionId from login step; allow from state or localStorage fallback
  const [email] = React.useState(
    () => location.state?.email || localStorage.getItem("mfa_email") || ""
  );
  const [sessionId] = React.useState(
    () => location.state?.sessionId || localStorage.getItem("mfa_session") || ""
  );
  const [totpSetup] = React.useState(
    () =>
      Boolean(location.state?.totpSetup) ||
      localStorage.getItem("mfa_totpSetup") === "1"
  );
  const [qr, setQr] = React.useState(null);
  const [code, setCode] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!email || !sessionId) {
      navigate("/login");
      return;
    }
    // If user doesn't have TOTP set up, get a QR
    if (!totpSetup) {
      (async () => {
        try {
          const apiUrl = process.env.REACT_APP_API_URL || "";
          const res = await fetch(`${apiUrl.replace(/\/$/, "")}/setup-totp`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || "Failed to setup TOTP");
          setQr(data.qrCode);
        } catch (e) {
          setError(e.message);
        }
      })();
    }
  }, [email, sessionId, totpSetup, navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const res = await fetch(`${apiUrl.replace(/\/$/, "")}/mfa/totp-verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, sessionId, token: code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Verification failed");
      localStorage.setItem("token", data.token);
      // Clear temp mfa storage
      localStorage.removeItem("mfa_email");
      localStorage.removeItem("mfa_session");
      localStorage.removeItem("mfa_totpSetup");
      navigate("/dashboard");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 420, margin: "40px auto", padding: 24 }}>
      <h1 style={{ textAlign: "center" }}>Two‑Factor Authentication</h1>
      <p style={{ color: "#475569" }}>
        Enter the 6‑digit code from your authenticator app.
      </p>
      {!totpSetup && (
        <div
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            padding: 16,
            marginBottom: 16,
            textAlign: "center",
          }}
        >
          <div style={{ marginBottom: 8, fontWeight: 600 }}>
            Scan this QR to set up
          </div>
          {qr ? (
            <img src={qr} alt="TOTP QR" style={{ width: 220, height: 220 }} />
          ) : (
            <div>Generating QR…</div>
          )}
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 8 }}>
            Use Google Authenticator, Authy, or another TOTP app.
          </div>
        </div>
      )}
      <form
        onSubmit={handleVerify}
        style={{ display: "flex", flexDirection: "column", gap: 12 }}
      >
        <input
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          placeholder="123456"
          value={code}
          onChange={(e) =>
            setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))
          }
          required
          style={{
            padding: 12,
            fontSize: 18,
            letterSpacing: 6,
            textAlign: "center",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
          }}
        />
        <button
          type="submit"
          disabled={loading || code.length !== 6}
          style={{
            padding: 12,
            background: "#2563eb",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {loading ? "Verifying…" : "Verify"}
        </button>
      </form>
      {error && <div style={{ color: "#b91c1c", marginTop: 10 }}>{error}</div>}
    </div>
  );
};

export default MFA;
